import { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useAuth, formatApiErrorDetail } from '../contexts/AuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { User, Lock, AlertCircle } from 'lucide-react';

export default function LoginPage() {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const { login } = useAuth();
  const navigate = useNavigate();

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    try {
      await login(email, password);
      navigate('/dashboard');
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex">
      {/* 左側：ログインフォーム */}
      <div className="flex-1 flex items-center justify-center p-8 bg-background">
        <div className="w-full max-w-md animate-fade-in">
          <div className="mb-8">
            <div className="flex items-center gap-3 mb-2">
              <img src="/logo192.png" alt="CLSS-SaaS" className="h-12 w-12 rounded" />
              <h1 className="text-3xl font-bold tracking-tight text-foreground">
                CLSS-SaaS
              </h1>
            </div>
            <p className="text-muted-foreground">
              津市対応 SaaS型 利用者管理・請求管理システム
            </p>
          </div>

          <Card className="card-surface">
            <CardHeader>
              <CardTitle className="text-xl">ログイン</CardTitle>
              <CardDescription>
                メールアドレスとパスワードを入力してください
              </CardDescription>
            </CardHeader>
            <CardContent>
              <form onSubmit={handleSubmit} className="space-y-4">
                {error && (
                  <div 
                    className="flex items-center gap-2 p-3 rounded-lg bg-[hsl(var(--error-bg))] text-[hsl(var(--error))] text-sm"
                    data-testid="login-error-message"
                  >
                    <AlertCircle size={18} />
                    <span>{error}</span>
                  </div>
                )}

                <div className="space-y-2">
                  <Label htmlFor="email" className="text-sm font-medium">
                    メールアドレス
                  </Label>
                  <div className="relative">
                    <User 
                      size={18} 
                      className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" 
                    />
                    <Input
                      id="email"
                      type="email"
                      placeholder="example@email.com"
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      className="pl-10"
                      required
                      data-testid="login-email-input"
                    />
                  </div>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="password" className="text-sm font-medium">
                    パスワード
                  </Label>
                  <div className="relative">
                    <Lock 
                      size={18} 
                      className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" 
                    />
                    <Input
                      id="password"
                      type="password"
                      placeholder="••••••••"
                      value={password}
                      onChange={(e) => setPassword(e.target.value)}
                      className="pl-10"
                      required
                      data-testid="login-password-input"
                    />
                  </div>
                </div>

                <Button 
                  type="submit" 
                  className="w-full btn-active"
                  disabled={loading}
                  data-testid="login-submit-button"
                >
                  {loading ? 'ログイン中...' : 'ログイン'}
                </Button>
              </form>

              <div className="mt-6 text-center text-sm text-muted-foreground">
                アカウントをお持ちでない場合は{' '}
                <Link 
                  to="/register" 
                  className="text-primary hover:underline font-medium"
                  data-testid="register-link"
                >
                  新規登録
                </Link>
              </div>
            </CardContent>
          </Card>
        </div>
      </div>

      {/* 右側：背景画像 */}
      <div 
        className="hidden lg:block lg:w-1/2 bg-cover bg-center relative"
        style={{
          backgroundImage: `url('https://images.pexels.com/photos/32911574/pexels-photo-32911574.jpeg')`,
        }}
      >
        <div className="absolute inset-0 bg-black/10" />
        <div className="absolute bottom-8 left-8 right-8 text-white">
          <p className="text-lg font-medium drop-shadow-lg">
            「入力するだけで業務が完結する福祉SaaS」
          </p>
          <p className="text-sm opacity-80 mt-2 drop-shadow">
            三重県津市の地域生活支援事業・日中一時支援・移動支援に対応
          </p>
        </div>
      </div>
    </div>
  );
}
